import { Close } from '@mui/icons-material'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Slide,
  Button,
  IconButton,
  Typography,
  TextField,
  Box,
  CircularProgress,
} from '@mui/material'
import type { TransitionProps } from '@mui/material/transitions'
import React, { useState, useEffect, useRef } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { patchDevice } from '@/api/Device'
import type { Device } from '@/pages/Device/Device'
import { enqueueSnackbar } from 'notistack'

const Transition = React.forwardRef<
  unknown,
  TransitionProps & { children: React.ReactElement }
>((props, ref) => <Slide direction="down" ref={ref} {...props} />)
const serialSchema = Yup.object({
  serial: Yup.string()
    .max(32, 'Serial number must be at most 32 characters')
    .required('Serial number is required'),
})
Transition.displayName = 'Transition'

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  device: Device | null
  onConfirm: (updatedDevice: Device) => void
  onCancel?: () => void
  onClose?: () => void
}

const SerialEntryDialog = (props: Props): JSX.Element => {
  const {
    open,
    setOpen,
    device,
    onConfirm,
    onCancel = () => {},
    onClose = () => {},
  } = props

  const { t } = useTranslation(['di', 'common', 'app'])
  const [serial, setSerial] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setSerial('')
      setValidationError(null)
      const timer = setTimeout(() => inputRef.current?.focus(), 50)
      return () => clearTimeout(timer)
    }
  }, [open])

  const validate = (value: string): boolean => {
    try {
      serialSchema.validateSync({ serial: value })
      setValidationError(null)
      return true
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setValidationError(err.message)
      }
      return false
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSerial(value)

    if (validationError) validate(value)
  }

  const onConfirmClick = async () => {
    if (!device) return
    if (!validate(serial)) return

    setSaving(true)
    try {
      const updatedDevice = await patchDevice(device, { serial: serial.trim() })
      enqueueSnackbar(
        t('app:device.calibration.missingData.updatedSuccessfully'),
        { variant: 'success' },
      )
      setOpen(false)
      onConfirm(updatedDevice)
    } catch {
      // Caught by global interceptor
    } finally {
      setSaving(false)
    }
  }

  const onCancelClick = () => {
    if (saving) return
    setOpen(false)
    onCancel()
  }

  const onCloseInternal = () => {
    if (saving) return
    setOpen(false)
    onClose()
  }

  return (
    <Dialog
      data-testid="serial-entry-dialog"
      open={open}
      keepMounted
      onClose={onCloseInternal}
      transitionDuration={300}
      slots={{ transition: Transition }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: '100px',
        },
        '& .MuiDialog-paper': {
          borderRadius: '6px',
          padding: 0,
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.15)',
          height: 'fit-content',
          marginTop: '100px !important',
          overflow: 'visible',
          maxHeight: 'unset',
          position: 'relative',
        },
      }}
    >
      <DialogContent
        sx={{
          paddingTop: '24px',
          paddingRight: '24px',
          paddingBottom: '16px',
          paddingLeft: '24px',
        }}
      >
        <IconButton
          data-testid="serial-dialog-close-button"
          onClick={onCloseInternal}
          disabled={saving}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'grey.500',
          }}
        >
          <Close />
        </IconButton>

        <Typography
          component="h4"
          sx={{
            margin: 0,
            lineHeight: '1.42857143',
            paddingRight: 4,
            fontSize: '1.25rem',
            fontWeight: 'inherit',
          }}
        >
          {t('app:device.calibration.missingData.title')}
        </Typography>
        <Box sx={{ mt: 3 }}>
          <TextField
            inputRef={inputRef}
            data-testid="serial-dialog-input"
            fullWidth
            label={t('di:device.serialNr')}
            value={serial}
            onChange={handleChange}
            onKeyDown={(e) => e.key === 'Enter' && onConfirmClick()}
            disabled={saving}
            error={Boolean(validationError)}
            helperText={validationError ?? ' '}
            slotProps={{
              htmlInput: { maxLength: 32, autoComplete: 'off' },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'flex-end',
          padding: 2,
          paddingTop: 0,
          gap: 1,
        }}
      >
        <Button
          data-testid="serial-dialog-cancel-button"
          onClick={onCancelClick}
          disabled={saving}
          sx={{
            color: 'grey.700',
            fontWeight: 500,
            backgroundColor: 'grey.300',
            '&:hover': { backgroundColor: 'grey.400' },
            textTransform: 'uppercase',
            minWidth: '80px',
          }}
        >
          {t('common:general.cancel')}
        </Button>
        <Button
          data-testid="serial-dialog-confirm-button"
          onClick={onConfirmClick}
          disabled={saving || !serial.trim()}
          color="success"
          variant="contained"
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : undefined
          }
          sx={{
            backgroundColor: '#4caf50',
            '&:hover': { backgroundColor: '#43a047' },
            fontWeight: 500,
            textTransform: 'uppercase',
            minWidth: '80px',
          }}
        >
          {t('common:form.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SerialEntryDialog
